import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import api, { formatApiErrorDetail } from "@/lib/api";
import { LEAD_STAGES, selectCls, btnBrand } from "@/lib/academyFormat";
import { ACADEMY } from "@/constants/testIds";

const EMPTY = { name: "", phone: "", email: "", course_id: "", source: "", executive_id: "", stage: "new", note: "", lost_reason: "" };

// Fix U (2026-10-13): alta y edicion de un interesado de la academia.
export default function LeadModal({ open, onOpenChange, lead, courses, executives, sources, canAssign, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const isEdit = Boolean(lead);

  useEffect(() => {
    if (!open) return;
    setError("");
    setForm(lead ? {
      name: lead.name || "", phone: lead.phone || "", email: lead.email || "", course_id: lead.course_id || "",
      source: lead.source || "", executive_id: lead.executive_id || "", stage: lead.stage || "new", note: "",
      lost_reason: lead.lost_reason || "",
    } : EMPTY);
  }, [open, lead]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (form.name.trim().length < 2 || form.phone.trim().length < 6) {
      setError("Nombre y teléfono son obligatorios");
      return;
    }
    setSaving(true);
    setError("");
    const body = {
      name: form.name.trim(), phone: form.phone.trim(), email: form.email.trim() || null,
      course_id: form.course_id || null, source: form.source || null, stage: form.stage,
    };
    if (canAssign) body.executive_id = form.executive_id || null;
    if (form.stage === "lost") body.lost_reason = form.lost_reason.trim() || null;
    try {
      let data;
      if (isEdit) {
        if (!canAssign || body.executive_id === lead.executive_id || !body.executive_id) delete body.executive_id;
        if (!body.email) delete body.email;
        if (!body.course_id) delete body.course_id;
        ({ data } = await api.put(`/academy/leads/${lead.id}`, body));
      } else {
        if (form.note.trim()) body.note = form.note.trim();
        if (!body.executive_id) delete body.executive_id;
        ({ data } = await api.post("/academy/leads", body));
      }
      onSaved(data);
      onOpenChange(false);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid={ACADEMY.leadModal}>
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">{isEdit ? "Editar interesado" : "Nuevo interesado"}</DialogTitle>
          <DialogDescription className="sr-only">Datos del interesado</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Nombre</Label>
            <Input value={form.name} onChange={set("name")} data-testid={ACADEMY.leadNameInput} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm text-muted-foreground mb-1 block">Teléfono (WhatsApp)</Label>
              <Input value={form.phone} onChange={set("phone")} placeholder="+1 713 555 0101" data-testid={ACADEMY.leadPhoneInput} />
            </div>
            <div>
              <Label className="text-sm text-muted-foreground mb-1 block">Email</Label>
              <Input type="email" value={form.email} onChange={set("email")} placeholder="Para mandarle la inscripción" data-testid={ACADEMY.leadEmailInput} />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm text-muted-foreground mb-1 block">Curso de interés</Label>
              <select className={selectCls} value={form.course_id} onChange={set("course_id")} data-testid={ACADEMY.leadCourseSelect}>
                <option value="">Sin definir</option>
                {courses.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-sm text-muted-foreground mb-1 block">Cómo llegó</Label>
              <select className={selectCls} value={form.source} onChange={set("source")} data-testid={ACADEMY.leadSourceSelect}>
                <option value="">Sin definir</option>
                {sources.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          {canAssign && (
            <div>
              <Label className="text-sm text-muted-foreground mb-1 block">Ejecutivo</Label>
              <select className={selectCls} value={form.executive_id} onChange={set("executive_id")} data-testid={ACADEMY.leadExecutiveSelect}>
                <option value="">Sin asignar</option>
                {executives.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
              </select>
            </div>
          )}
          {isEdit && (
            <div>
              <Label className="text-sm text-muted-foreground mb-1 block">Etapa</Label>
              <select className={selectCls} value={form.stage} onChange={set("stage")} data-testid={ACADEMY.leadStageSelect}>
                {LEAD_STAGES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </div>
          )}
          {isEdit && form.stage === "lost" && (
            <div>
              <Label className="text-sm text-muted-foreground mb-1 block">Por qué se perdió</Label>
              <Input value={form.lost_reason} onChange={set("lost_reason")} placeholder="Ej: precio, fecha, no respondió" />
            </div>
          )}
          {!isEdit && (
            <div>
              <Label className="text-sm text-muted-foreground mb-1 block">Primera nota</Label>
              <Textarea value={form.note} onChange={set("note")} rows={3} placeholder="Qué preguntó, qué le respondiste" data-testid={ACADEMY.leadNoteInput} />
            </div>
          )}
          {error && <p className="text-sm text-destructive" data-testid={ACADEMY.modalError}>{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button className={btnBrand} onClick={save} disabled={saving} data-testid={ACADEMY.leadSaveButton}>{saving ? "Guardando..." : "Guardar"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
